import * as React from 'react';
import ReactDOM from 'react-dom';
import { isNil } from 'lodash';

import findScrollParent from 'utils/findScrollParent';

/* Component for monitoring the visibility of a list item within a scrollable
 * view. It differs from ScrollWatch in that it calls its callbacks at a set
 * frequency, rather than when visibility changes.
 *
 * Note: If you're looking for a component to load more items in a list, you
 * might want to consider ScrollLoadZone instead.
 * 
 * Usage:
 * <ul>
 *   ...
 *   <li>
 *     <VisibilityWatch interval={500} whileVisible={this.notifyItemVisible}>
 *       {renderItem()}
 *     </VisibilityWatch>
 *   </li>
 *   ...
 * </ul>
 */

type Visibility = 'above' | 'below' | 'visible';

class VisibilityWatch extends React.Component<{
  whileVisible?: Function;
  whileInvisible?: Function;
  interval: number;
  children: React.ReactNode;
}> {
  static defaultProps = {
    interval: 0,
    children: <div />,
  };

  componentDidMount() {
    this._scrollParent = findScrollParent(ReactDOM.findDOMNode(this));
    this._watcher = window.setInterval(this.handleVisibility, this.props.interval);
  }

  componentWillUnmount() {
    if (this._watcher) {
      window.clearInterval(this._watcher);
    }
  }

  _scrollParent: Node | Window | null = null;
  _watcher: number | null = null;

  handleVisibility = () => {
    const visibility = this.getVisibility();
    if (visibility === 'visible') {
      if (this.props.whileVisible) {
        this.props.whileVisible();
      }
    } else {
      if (this.props.whileInvisible) {
        this.props.whileInvisible();
      }
    }
  };

  distanceToTopOfScrollParent = (node: HTMLElement): number => {
    if (this._scrollParent !== window && !node.offsetParent) {
      throw new Error(
        'VisibilityWatch requires a scroll parent with `position: relative` in order to calculate its visibility.'
      );
    }
    if (
      node.offsetParent === this._scrollParent ||
      !node.offsetParent ||
      !(node.offsetParent instanceof HTMLElement)
    ) {
      return node.offsetTop;
    } else {
      return node.offsetTop + this.distanceToTopOfScrollParent(node.offsetParent);
    }
  };

  getVisibility = (): Visibility | undefined => {
    const node = ReactDOM.findDOMNode(this);
    if (node instanceof HTMLElement) {
      const top = this.distanceToTopOfScrollParent(node);
      const height = node.offsetHeight;

      let parentHeight;
      let parentScrollTop;

      if (this._scrollParent === window) {
        parentHeight = window.innerHeight;
        parentScrollTop = window.pageYOffset;
      } else if (this._scrollParent instanceof HTMLElement) {
        parentHeight = this._scrollParent.offsetHeight;
        parentScrollTop = this._scrollParent.scrollTop;
      }

      if (isNil(parentScrollTop) || isNil(parentHeight)) {
        console.error('VisibilityWatch scrollParent was an invalid type');
        return;
      }

      if (top + height < parentScrollTop) {
        return 'above';
      } else if (top > parentScrollTop + parentHeight) {
        return 'below';
      } else {
        return 'visible';
      }
    }

    console.error('VisibilityWatch findDOMNode returned non HTMLElement');
  };

  render() {
    return React.Children.only(this.props.children);
  }
}
export default VisibilityWatch;
